import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminHeader from '../../components/navigation/AdminHeader';
import FloorTypesTab from './components/FloorTypesTab';
import ColorsTab from './components/ColorsTab';
import RoomsTab from './components/RoomsTab';
import FinishesTab from './components/FinishesTab';
import Icon from '../../components/AppIcon';

const GlobalDefaultsManagement = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('floor-types');
  const [floorTypes, setFloorTypes] = useState([
  {
    id: 1,
    name: 'Metallic Epoxy',
    description: 'Premium metallic finish with unique swirling patterns that create a luxurious three-dimensional appearance',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1fe49e850-1765763743651.png",
    thumbnailAlt: 'Metallic epoxy floor showing silver and blue swirling patterns with glossy reflective finish',
    priceTemplate: '$8-12 per sq ft',
    usageCount: 12
  },
  {
    id: 2,
    name: 'Flake Epoxy',
    description: 'Decorative colored flakes embedded in clear epoxy coating for texture and visual interest',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1fde8d284-1766029957550.png",
    thumbnailAlt: 'Flake epoxy floor with multicolored decorative chips in gray base showing textured surface',
    priceTemplate: '$5-8 per sq ft',
    usageCount: 18
  },
  {
    id: 3,
    name: 'Solid Color Epoxy',
    description: 'Classic single-color epoxy coating providing uniform appearance and excellent durability',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1e5ce5ddd-1764775753935.png",
    thumbnailAlt: 'Solid color epoxy floor in deep gray with smooth glossy finish in commercial space',
    priceTemplate: '$4-6 per sq ft',
    usageCount: 25
  }]
  );

  const [colors, setColors] = useState([
  {
    id: 1,
    name: 'Midnight Blue',
    hexCode: '#1e3a8a',
    swatchImage: "https://img.rocket.new/generatedImages/rocket_gen_img_10b54adfa-1764989569967.png",
    swatchImageAlt: 'Deep midnight blue epoxy color swatch showing rich navy tone with slight metallic sheen',
    category: 'base',
    usageCount: 15
  },
  {
    id: 2,
    name: 'Charcoal Gray',
    hexCode: '#374151',
    swatchImage: "https://img.rocket.new/generatedImages/rocket_gen_img_173f07443-1764663671036.png",
    swatchImageAlt: 'Charcoal gray epoxy swatch displaying neutral dark gray with matte texture',
    category: 'base',
    usageCount: 22
  },
  {
    id: 3,
    name: 'Pearl White',
    hexCode: '#f8fafc',
    swatchImage: "https://img.rocket.new/generatedImages/rocket_gen_img_18b052bb1-1765271046605.png",
    swatchImageAlt: 'Pearl white epoxy color showing bright clean white with subtle pearl shimmer',
    category: 'base',
    usageCount: 18
  },
  {
    id: 4,
    name: 'Silver Flake',
    hexCode: '#94a3b8',
    swatchImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1fe49e850-1765763743651.png",
    swatchImageAlt: 'Silver metallic flake particles showing reflective silver chips for epoxy floors',
    category: 'flake',
    usageCount: 20
  },
  {
    id: 5,
    name: 'Copper Flake',
    hexCode: '#b45309',
    swatchImage: "https://img.rocket.new/generatedImages/rocket_gen_img_17ded70ab-1765028283599.png",
    swatchImageAlt: 'Copper colored decorative flakes displaying warm metallic bronze particles',
    category: 'flake',
    usageCount: 14
  },
  {
    id: 6,
    name: 'Gold Accent',
    hexCode: '#eab308',
    swatchImage: "https://images.unsplash.com/photo-1730651068869-2aea06e5a095",
    swatchImageAlt: 'Gold metallic accent color showing bright golden yellow with reflective finish',
    category: 'accent',
    usageCount: 10
  }]
  );

  const [rooms, setRooms] = useState([
  {
    id: 1,
    name: 'Modern Kitchen',
    category: 'residential',
    backgroundImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1785173a3-1765180551561.png",
    backgroundImageAlt: 'Modern kitchen interior with white cabinets and empty floor space ready for epoxy visualization',
    dimensions: '15x12 ft',
    usageCount: 16
  },
  {
    id: 2,
    name: 'Garage Workshop',
    category: 'residential',
    backgroundImage: "https://img.rocket.new/generatedImages/rocket_gen_img_193ee2927-1764798924046.png",
    backgroundImageAlt: 'Empty residential garage with concrete floor and tool storage ready for epoxy coating',
    dimensions: '20x20 ft',
    usageCount: 28
  },
  {
    id: 3,
    name: 'Retail Showroom',
    category: 'commercial',
    backgroundImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1ed342e3b-1765957069841.png",
    backgroundImageAlt: 'Commercial retail space with open floor plan and display areas showing empty floor',
    dimensions: '40x30 ft',
    usageCount: 12
  },
  {
    id: 4,
    name: 'Warehouse Floor',
    category: 'industrial',
    backgroundImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1ecdfb29a-1764699974639.png",
    backgroundImageAlt: 'Large industrial warehouse interior with concrete floor and storage racks',
    dimensions: '100x80 ft',
    usageCount: 8
  }]
  );

  const [finishes, setFinishes] = useState([
  {
    id: 1,
    name: 'High Gloss',
    description: 'Mirror-like reflective finish that maximizes light reflection and creates a luxurious appearance. Best for showrooms and high-end residential spaces.',
    shineLevel: 95,
    durability: 'High traffic commercial areas',
    usageCount: 20
  },
  {
    id: 2,
    name: 'Satin Matte',
    description: 'Low-sheen finish that reduces glare while maintaining durability. Ideal for industrial and workshop environments where slip resistance is important.',
    shineLevel: 25,
    durability: 'Industrial and workshop spaces',
    usageCount: 15
  },
  {
    id: 3,
    name: 'Eggshell',
    description: 'Subtle sheen finish that balances aesthetics and practicality. Perfect for residential garages and moderate traffic commercial spaces.',
    shineLevel: 55,
    durability: 'Moderate traffic areas',
    usageCount: 18
  }]
  );

  useEffect(() => {
    const adminToken = localStorage.getItem('adminToken');
    if (!adminToken) {
      navigate('/admin-login');
    }
  }, [navigate]);

  const tabs = [
  { id: 'floor-types', label: 'Floor Types', icon: 'Grid3x3' },
  { id: 'colors', label: 'Colors', icon: 'Palette' },
  { id: 'rooms', label: 'Rooms', icon: 'Home' },
  { id: 'finishes', label: 'Finishes', icon: 'Sparkles' }];


  const handleAddFloorType = (newItem) => {
    setFloorTypes([...floorTypes, newItem]);
  };

  const handleEditFloorType = (updatedItem) => {
    setFloorTypes(floorTypes?.map((item) => item?.id === updatedItem?.id ? updatedItem : item));
  };

  const handleDeleteFloorType = (id) => {
    setFloorTypes(floorTypes?.filter((item) => item?.id !== id));
  };

  const handleAddColor = (newItem) => {
    setColors([...colors, newItem]);
  };

  const handleEditColor = (updatedItem) => {
    setColors(colors?.map((item) => item?.id === updatedItem?.id ? updatedItem : item));
  };

  const handleDeleteColor = (id) => {
    setColors(colors?.filter((item) => item?.id !== id));
  };

  const handleAddRoom = (newItem) => {
    setRooms([...rooms, newItem]);
  };

  const handleEditRoom = (updatedItem) => {
    setRooms(rooms?.map((item) => item?.id === updatedItem?.id ? updatedItem : item));
  };

  const handleDeleteRoom = (id) => {
    setRooms(rooms?.filter((item) => item?.id !== id));
  };

  const handleAddFinish = (newItem) => {
    setFinishes([...finishes, newItem]);
  };

  const handleEditFinish = (updatedItem) => {
    setFinishes(finishes?.map((item) => item?.id === updatedItem?.id ? updatedItem : item));
  };

  const handleDeleteFinish = (id) => {
    setFinishes(finishes?.filter((item) => item?.id !== id));
  };

  return (
    <div className="min-h-screen bg-background">
      <AdminHeader />
      <div className="main-content">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-text-primary mb-2">Global Defaults Management</h1>
            <p className="text-text-secondary">
              Configure system-wide default options for floor types, colors, rooms, and finishes
            </p>
          </div>

          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="border-b border-border overflow-x-auto">
              <nav className="flex gap-1 px-6">
                {tabs?.map((tab) =>
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`
                      flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-all duration-150 whitespace-nowrap
                      ${activeTab === tab?.id ?
                  'text-primary border-primary' : 'text-text-secondary border-transparent hover:text-text-primary hover:border-muted'}
                    `
                  }>

                    <Icon name={tab?.icon} size={18} />
                    <span>{tab?.label}</span>
                  </button>
                )}
              </nav>
            </div>

            <div className="p-6">
              {activeTab === 'floor-types' &&
              <FloorTypesTab
                floorTypes={floorTypes}
                onAdd={handleAddFloorType}
                onEdit={handleEditFloorType}
                onDelete={handleDeleteFloorType} />

              }

              {activeTab === 'colors' &&
              <ColorsTab
                colors={colors}
                onAdd={handleAddColor}
                onEdit={handleEditColor}
                onDelete={handleDeleteColor} />

              }

              {activeTab === 'rooms' &&
              <RoomsTab
                rooms={rooms}
                onAdd={handleAddRoom}
                onEdit={handleEditRoom}
                onDelete={handleDeleteRoom} />

              }

              {activeTab === 'finishes' &&
              <FinishesTab
                finishes={finishes}
                onAdd={handleAddFinish}
                onEdit={handleEditFinish}
                onDelete={handleDeleteFinish} />

              }
            </div>
          </div>
        </div>
      </div>
    </div>);

};

export default GlobalDefaultsManagement;